import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { RentalApi } from 'api';
import type { Rental } from 'api/methods/RentalApi';

/**
 * [Rental] Query key 생성
 * --
 */
export const rentalQueryKeys = createQueryKeys('rental', {
  getRental: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getRentals: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [Rental] 렌탈 전체 조회
 * --
 */
export const useGetRentals = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: rentalQueryKeys.getRentals(params).queryKey,
    queryFn: () => RentalApi.getRentals(params),
    ...options,
  });
  return data;
};

/**
 * [Rental] 렌탈 상세 조회
 * --
 */
export const useGetRental = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: rentalQueryKeys.getRental(idx, params).queryKey,
    queryFn: () => RentalApi.getRental(idx, params),
  });

  return data;
};

/**
 * [Rental] 렌탈 생성
 * --
 */
export const useCreateRental = (body: Rental) => {
  const { mutateAsync: createRental, ...result } = useMutation({
    mutationFn: (variables: Rental) => RentalApi.createRental(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createRental, ...result };
};

/**
 * [Rental] 렌탈 수정
 * --
 */
export const useUpdateRental = (body: Rental) => {
  const { mutateAsync: updateRental, ...result } = useMutation({
    mutationFn: (variables: Rental) => RentalApi.updateRental(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { updateRental, ...result };
};

/**
 * [Rental] 렌탈 삭제
 * --
 */
export const useDeleteRental = (idx: number) => {
  const { mutateAsync: deleteRental, ...result } = useMutation({
    mutationFn: (variables: number) => RentalApi.deleteRental(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { deleteRental, ...result };
};

/**
 * [Rental] 렌탈 벌크 생성
 * --
 */
export const useBulkCreateRental = () => {
  const { mutateAsync: bulkCreateRental, ...result } = useMutation({
    mutationFn: (variables: Rental[]) => RentalApi.bulkCreateRental(variables),
  });

  return { bulkCreateRental, ...result };
};

/**
 * [Rental] 렌탈 벌크 수정
 * --
 */
export const useBulkUpdateRental = () => {
  const { mutateAsync: bulkUpdateRental, ...result } = useMutation({
    mutationFn: (variables: Rental[]) => RentalApi.bulkUpdateRental(variables),
  });

  return { bulkUpdateRental, ...result };
};

/**
 * [Rental] 렌탈 벌크 삭제
 * --
 */
export const useBulkDeleteRental = () => {
  const { mutateAsync: bulkDeleteRental, ...result } = useMutation({
    mutationFn: (variables: Rental[]) => RentalApi.bulkDeleteRental(variables),
  });

  return { bulkDeleteRental, ...result };
};

/**
 * [Rental] 렌탈 상태 수정
 * --
 */
export const useUpdateStatusRental = () => {
  const { mutateAsync: updateStatusRental, ...result } = useMutation({
    mutationFn: (variables: Rental) => RentalApi.updateStatusRental(variables),
  });

  return { updateStatusRental, ...result };
};

/**
 * [Rental] 렌탈 구매확정
 * --
 */
export const useUpdateConfirmRental = () => {
  const { mutateAsync: updateConfirmRental, ...result } = useMutation({
    mutationFn: (variables: Rental) => RentalApi.updateConfirmRental(variables),
  });

  return { updateConfirmRental, ...result };
};
